import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Modal from "../../../Components/Modal/Modal";
import { fetchTemplateItems } from "../Reducers/actions";
import "./TemplateItemsBuilder.css";

const TemplateItemsBuilder = ({ templateId, chapters, onSubmit, onBack }) => {
  const dispatch = useDispatch();
  const templateItemsFromStore = useSelector(state => state.zwembadinspecties.templateItems);
  const [items, setItems] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(chapters && chapters.length > 0 ? chapters[0]._id || 0 : null);
  const [expandedChapters, setExpandedChapters] = useState([0]); // First chapter open by default

  useEffect(() => {
    if (templateId) {
      dispatch(fetchTemplateItems(templateId));
    }
  }, [dispatch, templateId]);

  useEffect(() => {
    if (templateItemsFromStore && templateItemsFromStore.length > 0) {
      setItems(templateItemsFromStore);
    }
  }, [templateItemsFromStore]);

  const handleAddItem = (chapterId = null) => {
    const targetChapterId = chapterId !== null ? chapterId : selectedChapter;
    const chapterItems = items.filter(item => item.chapter_id === targetChapterId);

    setEditingItem({
      question: "",
      input_type: "text",
      chapter_id: targetChapterId,
      sort_order: chapterItems.length,
      is_required: false,
      help_text: "",
      options: []
    });
    setShowAddModal(true);
  };

  const getChapterItems = () => {
    return items.filter(item => item.chapter_id === selectedChapter);
  };

  const getCurrentChapter = () => {
    if (!chapters || chapters.length === 0) return null;
    return chapters.find(ch => (ch._id || chapters.indexOf(ch)) === selectedChapter);
  };

  const toggleChapter = (chapterIndex) => {
    if (expandedChapters.includes(chapterIndex)) {
      setExpandedChapters(expandedChapters.filter(idx => idx !== chapterIndex));
    } else {
      setExpandedChapters([...expandedChapters, chapterIndex]);
    }
  };

  const handleEditItem = (item, index) => {
    setEditingItem({ ...item, index });
    setShowAddModal(true);
  };

  const handleSaveItem = (item) => {
    if (item.index !== undefined) {
      // Edit bestaand item
      const newItems = [...items];
      newItems[item.index] = item;
      setItems(newItems);
    } else {
      // Voeg nieuw item toe - get the correct sort_order for this chapter
      const chapterItems = items.filter(i => i.chapter_id === item.chapter_id);
      const newItem = { ...item, sort_order: chapterItems.length };
      setItems([...items, newItem]);
    }
    setShowAddModal(false);
    setEditingItem(null);
  };

  const handleDeleteItem = (index) => {
    if (window.confirm("Weet je zeker dat je dit item wilt verwijderen?")) {
      const itemToDelete = items[index];
      const newItems = items.filter((_, i) => i !== index);

      // Update sort_order only for items in the same chapter
      const chapterItems = newItems.filter(item => item.chapter_id === itemToDelete.chapter_id);
      chapterItems.forEach((item, i) => {
        item.sort_order = i;
      });

      setItems(newItems);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    // Extract chapter ID from droppableId (format: "items-{chapterId}")
    const sourceChapterId = result.source.droppableId.replace('items-', '');
    const destChapterId = result.destination.droppableId.replace('items-', '');

    // Don't allow moving between chapters
    if (sourceChapterId !== destChapterId) return;

    // Get items for this chapter only
    const chapterItems = items.filter(item => String(item.chapter_id) === String(sourceChapterId));
    const otherItems = items.filter(item => String(item.chapter_id) !== String(sourceChapterId));

    // Reorder within chapter
    const [reorderedItem] = chapterItems.splice(result.source.index, 1);
    chapterItems.splice(result.destination.index, 0, reorderedItem);

    // Update sort_order for chapter items
    chapterItems.forEach((item, index) => {
      item.sort_order = index;
    });

    // Combine back
    setItems([...otherItems, ...chapterItems]);
  };

  const handleSubmit = () => {
    if (items.length === 0) {
      alert("Voeg minimaal één item toe aan het sjabloon.");
      return;
    }
    // Check if all chapters have items
    const chaptersWithoutItems = chapters.filter(ch => {
      const chId = ch._id || chapters.indexOf(ch);
      return items.filter(item => item.chapter_id === chId).length === 0;
    });

    if (chaptersWithoutItems.length > 0) {
      const chapterNames = chaptersWithoutItems.map(ch => ch.title).join(", ");
      if (!window.confirm(`De volgende hoofdstukken hebben nog geen items: ${chapterNames}. Toch doorgaan?`)) {
        return;
      }
    }

    onSubmit(items);
  };

  const getInputTypeIcon = (type) => {
    const icons = {
      text: "fas fa-font",
      textarea: "fas fa-align-left",
      number: "fas fa-hashtag",
      checkbox: "fas fa-check-square",
      date: "fas fa-calendar",
      select: "fas fa-list",
      file: "fas fa-file-upload"
    };
    return icons[type] || "fas fa-question";
  };

  const getInputTypeLabel = (type) => {
    const labels = {
      text: "Tekst",
      textarea: "Tekstveld",
      number: "Nummer",
      checkbox: "Checkbox",
      date: "Datum",
      select: "Dropdown",
      file: "Bestand"
    };
    return labels[type] || type;
  };

  return (
    <div className="template-items-builder">
      <div className="mb-4">
        <h4>Items per Hoofdstuk</h4>
        <p className="text-muted">
          Voeg items toe aan elk hoofdstuk. Elk hoofdstuk kan uitklapt worden om de items te bekijken en bewerken.
        </p>
      </div>

      {/* Chapter Accordion */}
      {chapters && chapters.length > 0 ? (
        <div className="accordion" id="chaptersAccordion">
          {chapters.map((chapter, chapterIndex) => {
            const chapterId = chapter._id || chapterIndex;
            const chapterItems = items.filter(item => item.chapter_id === chapterId);
            const accordionId = `chapter-${chapterIndex}`;

            return (
              <div className="card mb-3 shadow-sm" key={chapterIndex}>
                <div
                  className="card-header bg-light"
                  id={`heading-${accordionId}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    toggleChapter(chapterIndex);
                    setSelectedChapter(chapterId);
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="flex-grow-1">
                      <h5 className="mb-0 d-flex align-items-center text-dark">
                        <i
                          className={`fas fa-chevron-right mr-3 text-primary ${expandedChapters.includes(chapterIndex) ? 'fa-rotate-90' : ''}`}
                          style={{ transition: 'transform 0.2s' }}
                        ></i>
                        <span className="font-weight-bold">{chapterIndex + 1}. {chapter.title}</span>
                        <span className="badge badge-primary ml-3 px-3 py-1">
                          {chapterItems.length} {chapterItems.length === 1 ? 'item' : 'items'}
                        </span>
                      </h5>
                    </div>
                    <button
                      className="btn btn-primary btn-sm ml-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedChapter(chapterId);
                        handleAddItem(chapterId);
                      }}
                    >
                      <i className="fas fa-plus mr-1"></i> Nieuw Item
                    </button>
                  </div>
                </div>

                {expandedChapters.includes(chapterIndex) && (
                  <div>
                    <div className="card-body bg-white" style={{ padding: '1.5rem' }}>
                    {chapterItems.length === 0 ? (
                      <div className="alert alert-info mb-0" style={{ backgroundColor: '#e7f3ff', borderColor: '#b3d9ff' }}>
                        <i className="fas fa-info-circle mr-2"></i>
                        Nog geen items toegevoegd aan dit hoofdstuk. Klik op "Nieuw Item" om te beginnen.
                      </div>
                    ) : (
                      <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId={`items-${chapterId}`}>
                          {(provided) => (
                            <div
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              className="items-list"
                            >
                              {chapterItems.map((item, itemIndex) => {
                                // Find the global index of this item
                                const globalIndex = items.findIndex(i => i === item);
                                return (
                                  <Draggable
                                    key={globalIndex}
                                    draggableId={`item-${globalIndex}`}
                                    index={itemIndex}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className={`card mb-2 border ${snapshot.isDragging ? "shadow-lg border-primary" : "border-light"}`}
                                        style={{
                                          transition: 'all 0.2s',
                                          backgroundColor: snapshot.isDragging ? '#f8f9fa' : 'white'
                                        }}
                                      >
                                        <div className="card-body" style={{ padding: '1rem' }}>
                                          <div className="d-flex align-items-start">
                                            <div
                                              {...provided.dragHandleProps}
                                              className="drag-handle mr-3"
                                              style={{ cursor: "grab", padding: '0.5rem', color: '#6c757d' }}
                                            >
                                              <i className="fas fa-grip-vertical"></i>
                                            </div>
                                            <div className="flex-grow-1">
                                              <div className="d-flex justify-content-between align-items-start">
                                                <div className="flex-grow-1">
                                                  <h6 className="mb-2 font-weight-bold text-dark">
                                                    {itemIndex + 1}. {item.question}
                                                    {item.is_required && (
                                                      <span className="badge badge-danger ml-2 px-2 py-1">Verplicht</span>
                                                    )}
                                                  </h6>
                                                  <div className="text-muted small d-flex align-items-center">
                                                    <i className={`${getInputTypeIcon(item.input_type)} mr-2`}></i>
                                                    <span className="font-weight-medium">{getInputTypeLabel(item.input_type)}</span>
                                                    {item.help_text && (
                                                      <span className="ml-3">
                                                        <i className="fas fa-info-circle mr-1"></i> {item.help_text}
                                                      </span>
                                                    )}
                                                  </div>
                                                  {item.options && item.options.length > 0 && (
                                                    <div className="mt-2 p-2 bg-light rounded">
                                                      <small className="text-muted">
                                                        <strong>Opties:</strong> {item.options.map(opt => opt.label).join(", ")}
                                                      </small>
                                                    </div>
                                                  )}
                                                </div>
                                                <div className="btn-group ml-3">
                                                  <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => handleEditItem(item, globalIndex)}
                                                    title="Bewerken"
                                                  >
                                                    <i className="fas fa-edit"></i>
                                                  </button>
                                                  <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDeleteItem(globalIndex)}
                                                    title="Verwijderen"
                                                  >
                                                    <i className="fas fa-trash"></i>
                                                  </button>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle"></i> Geen hoofdstukken gevonden. Ga terug naar stap 2 om hoofdstukken toe te voegen.
        </div>
      )}

      <div className="d-flex justify-content-between mt-4">
        <button
          className="btn btn-secondary"
          onClick={onBack}
        >
          <i className="fas fa-arrow-left"></i> Vorige Stap
        </button>
        <button
          className="btn btn-success btn-lg"
          onClick={handleSubmit}
          disabled={items.length === 0}
        >
          <i className="fas fa-save"></i> Sjabloon Opslaan
        </button>
      </div>

      {/* Add/Edit Item Modal */}
      <Modal
        visible={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingItem(null);
        }}
        width={800}
      >
        <ItemModal
          item={editingItem}
          onSave={handleSaveItem}
          onCancel={() => {
            setShowAddModal(false);
            setEditingItem(null);
          }}
        />
      </Modal>
    </div>
  );
};

// Predefined Item Templates
const ITEM_TEMPLATES = [
  {
    id: 'custom',
    name: '-- Aangepast item --',
    description: 'Maak een volledig aangepast item'
  },
  {
    id: 'schaal_delen_glijdelen',
    name: 'Schaal delen glijdelen',
    description: 'Controle van schaal delen en glijdelen',
    config: {
      input_type: 'select',
      is_required: true,
      help_text: 'Beoordeel de staat van de schaal delen en glijdelen',
      options: [
        { label: 'Goed', value: 'goed' },
        { label: 'Matig', value: 'matig' },
        { label: 'Slecht', value: 'slecht' }
      ]
    }
  },
  {
    id: 'bevindingen',
    name: 'Bevindingen',
    description: 'Algemene bevindingen en opmerkingen',
    config: {
      input_type: 'textarea',
      help_text: 'Beschrijf de bevindingen',
      is_required: false
    }
  },
  {
    id: 'inspectieonderdelen',
    name: 'Inspectieonderdelen',
    description: 'Tabel met inspectieonderdelen, eisen, constateringen en scores',
    config: {
      input_type: 'select',
      is_required: false,
      help_text: 'Voeg inspectieonderdelen toe met eisen, constateringen en scores',
      options: []
    }
  }
];

// Item Modal Component
const ItemModal = ({ item, onSave, onCancel }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(item.item_template_id || 'custom');
  const [formData, setFormData] = useState(item);
  const [options, setOptions] = useState(item.options || []);
  const [inspectieonderdelenRows, setInspectieonderdelenRows] = useState(item.inspectieonderdelen_rows || []);

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);

    if (templateId === 'custom') {
      // Reset to basic form - clear item_template_id
      setFormData({
        ...formData,
        item_template_id: null
      });
      return;
    }

    const template = ITEM_TEMPLATES.find(t => t.id === templateId);
    if (template && template.config) {
      // Apply complete template config including input_type
      setFormData({
        ...formData,
        ...template.config,
        question: formData.question, // Keep the question field
        item_template_id: templateId // Save the template ID
      });

      // Set options if template has them
      if (template.config.options) {
        setOptions(template.config.options);
      }

      // Set inspectieonderdelen rows if template is inspectieonderdelen
      if (templateId === 'inspectieonderdelen' && item.inspectieonderdelen_rows) {
        setInspectieonderdelenRows(item.inspectieonderdelen_rows);
      } else if (templateId === 'inspectieonderdelen') {
        // Initialize with one empty row for new items
        setInspectieonderdelenRows([{
          inspectieonderdeel: "",
          eis_toelichting: ""
        }]);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value
    });
  };

  const handleAddOption = () => {
    setOptions([...options, { label: "", value: "" }]);
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...options];
    newOptions[index][field] = value;
    if (field === "label" && !newOptions[index].value) {
      // Auto-generate value from label
      newOptions[index].value = value.toLowerCase().replace(/\s+/g, "_");
    }
    setOptions(newOptions);
  };

  const handleRemoveOption = (index) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleAddInspectieonderdelenRow = () => {
    setInspectieonderdelenRows([...inspectieonderdelenRows, {
      inspectieonderdeel: "",
      eis_toelichting: ""
    }]);
  };

  const handleInspectieonderdelenRowChange = (index, field, value) => {
    const newRows = [...inspectieonderdelenRows];
    newRows[index][field] = value;
    setInspectieonderdelenRows(newRows);
  };

  const handleRemoveInspectieonderdelenRow = (index) => {
    setInspectieonderdelenRows(inspectieonderdelenRows.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // item_template_id is already in formData from handleTemplateChange
    onSave({
      ...formData,
      options,
      inspectieonderdelen_rows: inspectieonderdelenRows
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="p-4" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
        {/* Header */}
        <div className="mb-4">
          <h5 className="mb-2">
            <i className={`fas ${item.index !== undefined ? 'fa-edit' : 'fa-plus'} mr-2`}></i>
            {item.index !== undefined ? "Item Bewerken" : "Nieuw Item Toevoegen"}
          </h5>
          <p className="text-muted small mb-0">Vul de vraag in en kies een configuratie methode</p>
        </div>

        <div>
          {/* Vraag eerst */}
          <div className="form-group">
            <label className="font-weight-bold">
              Vraag / Item Tekst *
            </label>
            <input
              type="text"
              className="form-control"
              name="question"
              value={formData.question}
              onChange={handleChange}
              required
              placeholder="Bijv. Is het water helder en schoon?"
            />
          </div>

          <hr className="my-4" />

          <div>
            {/* Template Selection */}
            <div className="mb-3">
              <div className="card">
                <div className="card-header bg-light">
                  <h6 className="mb-0">
                    <i className="fas fa-magic mr-2"></i>
                    Optie 1: Gebruik Sjabloon
                  </h6>
                </div>
                <div className="card-body">
                  <select
                    className="form-control mb-3"
                    value={selectedTemplate}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                  >
                    {ITEM_TEMPLATES.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                  {selectedTemplate !== 'custom' && (
                    <div className="alert alert-success alert-sm mb-0">
                      <i className="fas fa-check-circle mr-2"></i>
                      <small>Sjabloon actief</small>
                    </div>
                  )}
                  {selectedTemplate === 'custom' && (
                    <small className="text-muted d-block">
                      <i className="fas fa-info-circle mr-1"></i>
                      Selecteer een sjabloon voor snelle setup
                    </small>
                  )}
                </div>
              </div>
            </div>

            {/* Manual Input Type Selection - Only show when custom is selected */}
            {selectedTemplate === 'custom' && (
              <div className="mb-3">
                <div className="card">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">
                      <i className="fas fa-sliders-h mr-2"></i>
                      Optie 2: Handmatig Configureren
                    </h6>
                  </div>
                  <div className="card-body">
                    <select
                      className="form-control mb-3"
                      name="input_type"
                      value={formData.input_type}
                      onChange={handleChange}
                      required
                    >
                      <option value="text">Tekst (korte tekst)</option>
                      <option value="textarea">Tekstveld (lange tekst)</option>
                      <option value="number">Nummer</option>
                      <option value="checkbox">Checkbox (ja/nee)</option>
                      <option value="date">Datum</option>
                      <option value="select">Dropdown (keuze lijst)</option>
                      <option value="file">Bestand Upload</option>
                    </select>
                    <small className="text-muted d-block">
                      <i className="fas fa-info-circle mr-1"></i>
                      Kies zelf het input type
                    </small>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Additional Settings */}
          <div className="mt-4">
            <h6 className="font-weight-bold mb-3">
              <i className="fas fa-cog mr-2"></i>
              Aanvullende Instellingen
            </h6>

            <div className="form-group">
              <label>Help Tekst (optioneel)</label>
              <input
                type="text"
                className="form-control"
                name="help_text"
                value={formData.help_text}
                onChange={handleChange}
                placeholder="Extra uitleg of instructies voor dit item"
              />
              <small className="text-muted">
                Deze tekst helpt de inspector bij het invullen
              </small>
            </div>

            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                name="is_required"
                checked={formData.is_required}
                onChange={handleChange}
                id="is_required"
              />
              <label className="form-check-label" htmlFor="is_required">
                Dit veld is verplicht
              </label>
            </div>
          </div>

          {formData.input_type === "select" && selectedTemplate !== 'inspectieonderdelen' && (
            <div className="mt-4">
              <label className="font-weight-bold mb-3">
                <i className="fas fa-list-ul mr-2"></i>
                Dropdown Opties
              </label>
              {options.map((option, index) => (
                <div key={index} className="input-group mb-2">
                  <div className="input-group-prepend">
                    <span className="input-group-text">
                      #{index + 1}
                    </span>
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Label (zichtbaar)"
                    value={option.label}
                    onChange={(e) => handleOptionChange(index, "label", e.target.value)}
                  />
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Waarde"
                    value={option.value}
                    onChange={(e) => handleOptionChange(index, "value", e.target.value)}
                  />
                  <div className="input-group-append">
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => handleRemoveOption(index)}
                      title="Verwijder optie"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={handleAddOption}
              >
                <i className="fas fa-plus mr-2"></i> Optie Toevoegen
              </button>
            </div>
          )}

          {selectedTemplate === 'inspectieonderdelen' && (
            <div className="mt-4">
              <label className="font-weight-bold mb-3">
                <i className="fas fa-table mr-2"></i>
                Inspectieonderdelen Rijen
              </label>
              <p className="text-muted small mb-3">
                Definieer hier de vaste inspectieonderdelen. De inspector vult later de constatering (groter/kleiner) en score in.
              </p>
              {inspectieonderdelenRows.map((row, index) => (
                <div key={index} className="card mb-3">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0 text-primary">
                        <i className="fas fa-hashtag mr-2"></i>
                        Item {index + 1}
                      </h6>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleRemoveInspectieonderdelenRow(index)}
                        title="Verwijder rij"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>

                    <div className="form-group">
                      <label className="font-weight-medium">Inspectieonderdeel *</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Bijv. Zwembadwater kwaliteit"
                        value={row.inspectieonderdeel}
                        onChange={(e) => handleInspectieonderdelenRowChange(index, "inspectieonderdeel", e.target.value)}
                        required
                      />
                    </div>

                    <div className="form-group mb-0">
                      <label className="font-weight-medium">Eis/Toelichting *</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        placeholder="Bijv. Het water moet helder zijn volgens norm XYZ"
                        value={row.eis_toelichting}
                        onChange={(e) => handleInspectieonderdelenRowChange(index, "eis_toelichting", e.target.value)}
                        required
                      />
                      <small className="text-muted">
                        De inspector vult later de constatering (groter/kleiner) en score (1/2/3) in
                      </small>
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={handleAddInspectieonderdelenRow}
              >
                <i className="fas fa-plus mr-2"></i> Rij Toevoegen
              </button>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="d-flex justify-content-end mt-4 pt-3 border-top">
          <button
            type="button"
            className="btn btn-secondary mr-2"
            onClick={onCancel}
          >
            <i className="fas fa-times mr-2"></i>
            Annuleren
          </button>
          <button
            type="submit"
            className="btn btn-primary"
          >
            <i className="fas fa-save mr-2"></i>
            {item.index !== undefined ? 'Opslaan' : 'Toevoegen'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default TemplateItemsBuilder;
